/**
 * Undo/redo history for block configuration edits.
 *
 * Only configuration changes are undoable: each entry records a block's configuration before and
 * after one saved edit, so undoing can send the earlier configuration back to the server. Adding,
 * moving and deleting blocks are not undoable; they clear the history, because entries recorded
 * before them may refer to blocks or positions that no longer exist.
 */

export interface ConfigEdit {
  blockId: string;
  /** Configuration JSON before the edit */
  before: string;
  /** Configuration JSON after the edit */
  after: string;
}

export interface EditHistory {
  past: ConfigEdit[];
  future: ConfigEdit[];
}

export const MAX_HISTORY = 100;

export function emptyHistory(): EditHistory {
  return { past: [], future: [] };
}

/** Adds an edit, dropping anything that could have been redone. No-op edits are ignored. */
export function recordEdit(history: EditHistory, edit: ConfigEdit): EditHistory {
  if (edit.before === edit.after) return history;
  const past = [...history.past, edit];
  return { past: past.slice(-MAX_HISTORY), future: [] };
}

/** Follows block ID changes (temporary IDs replaced by the server's). */
export function renameInHistory(history: EditHistory, mapping: Map<string, string>): EditHistory {
  if (mapping.size === 0) return history;
  const rename = (edit: ConfigEdit) =>
    mapping.has(edit.blockId) ? { ...edit, blockId: mapping.get(edit.blockId)! } : edit;
  return { past: history.past.map(rename), future: history.future.map(rename) };
}

/** Renames keys of a per-block record (e.g. last persisted configurations). */
export function renameKeys<T>(record: Record<string, T>, mapping: Map<string, string>): Record<string, T> {
  if (mapping.size === 0) return record;
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    result[mapping.get(key) ?? key] = value;
  }
  return result;
}
